import config from "../../common/Config";
import {
  encryptPayload,
  normalizeEncryptedApiResponse,
  plainObjectFromFormData,
} from "../../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../../utils/companySettingsCache";

export const EDITABLE_PRICE_COLUMNS = ["box_cost", "price", "piece_cost", "piece_price"];

const COLUMN_TO_FORM = {
  box_cost: "cost",
  price: "price",
  piece_cost: "piece_cost",
  piece_price: "split_price",
};

const scalar = (v) => (v == null ? "" : String(v));

const marginPct = (sale, cost) => {
  const s = Number(sale);
  const c = Number(cost);
  if (!Number.isFinite(s) || s <= 0 || !Number.isFinite(c) || s <= c) {
    return "";
  }
  return (((s - c) / s) * 100).toFixed(0);
};

const formatDiff = (sale, cost, previous) => {
  const s = Number(sale);
  const c = Number(cost);
  if (!Number.isFinite(s) || s <= 0 || !Number.isFinite(c)) {
    return previous ?? "";
  }
  const rounded = (((s - c) / s) * 100).toFixed(0);
  const prev = String(previous ?? "");
  if (prev.includes("%")) {
    return `${rounded}%`;
  }
  return rounded;
};

const fetchProductEdit = async (productId, authToken, useEncrypt) => {
  const formData = new FormData();
  formData.append("id", productId);
  if (useEncrypt) {
    const ep = encryptPayload({ id: productId });
    if (ep) {
      formData.append("encrypted_payload", ep);
    }
  }
  const response = await fetch(`${config.API_URL}/edit_products`, {
    method: "POST",
    headers: { authorization: `Bearer ${authToken}` },
    body: formData,
  });
  const data = await response.json();
  return normalizeEncryptedApiResponse(data, useEncrypt);
};

const buildUpdateFormData = (p, suppliers, productId, userId, formField, nextValue) => {
  const cost = formField === "cost" ? nextValue : p.cost;
  const price = formField === "price" ? nextValue : p.price;
  const pieceCost = formField === "piece_cost" ? nextValue : p.piece_cost;
  const splitPrice = formField === "split_price" ? nextValue : p.split_price;
  const fd = new FormData();
  const set = (key, value) => fd.set(key, scalar(value));

  set("id", productId);
  set("user_id", userId);
  set("code", p.code);
  set("name", p.name);
  set("details", p.details ?? p.product_details);
  set("slug", p.slug);
  set("size", p.size);
  set("pack", p.pack);
  set("hsn_code", p.hsn_code);
  set("min_alert_quantity", p.min_alert_quantity);
  set("max_alert_quantity", p.max_alert_quantity);
  set("maximum_order_quantity", p.maximum_order_quantity ?? "");
  set("length", p.length);
  set("length_unit", p.length_unit);
  set("width", p.width);
  set("width_unit", p.width_unit);
  set("height", p.height);
  set("height_unit", p.height_unit);
  set("cbm", p.cbm != null && String(p.cbm).trim() !== "" ? p.cbm : "0");
  set("gross_weight", p.gross_weight);
  set("net_weight", p.net_weight);
  set("bay", p.bay);
  set("rack", p.rack);
  set("part_number", p.part_number);
  set("inner_ean_number", p.inner_ean_number);
  set("outer_ean_number", p.outer_ean_number);
  set("pallete_ean", p.pallete_ean);
  set("new_arrival", p.new_arrival === "1" || p.new_arrival === 1 ? "1" : "0");
  set("is_discount_allow", p.is_discount_allow === "1" || p.is_discount_allow === 1 ? "1" : "0");
  set("percentage", p.percentage);
  set("featured", p.featured === "1" || p.featured === 1 ? "1" : "0");
  set("availableFor", String(p.availableFor) === "1" ? "1" : "0");
  set("split", p.split);
  set("cost", cost);
  set("price", price);
  set("price_1", p.price_1);
  set("price_2", p.price_2);
  set("price_3", p.price_3);
  set("price_4", p.price_4);
  set("price_5", p.price_5);
  set("piece_cost", pieceCost);
  set("split_price", splitPrice);
  set("piece_price1", p.piece_price1);
  set("piece_price2", p.piece_price2);
  set("piece_price3", p.piece_price3);
  set("piece_price4", p.piece_price4);
  set("piece_price5", p.piece_price5);
  set("band1_special_box_price", p.offer_box_price || p.band1_special_box_price || "");
  set("band1_special_piece_price", p.offer_piece_price || p.band1_special_piece_price || "");
  set("band2_special_box_price", p.offer_box_price1 || p.band2_special_box_price || "");
  set("band2_special_piece_price", p.offer_piece_price1 || p.band2_special_piece_price || "");
  set("band3_special_box_price", p.offer_box_price2 || p.band3_special_box_price || "");
  set("band3_special_piece_price", p.offer_piece_price2 || p.band3_special_piece_price || "");
  set("band4_special_box_price", p.offer_box_price3 || p.band4_special_box_price || "");
  set("band4_special_piece_price", p.offer_piece_price3 || p.band4_special_piece_price || "");
  set("band5_special_box_price", p.offer_box_price4 || p.band5_special_box_price || "");
  set("band5_special_piece_price", p.offer_piece_price4 || p.band5_special_piece_price || "");
  set("band6_special_box_price", p.offer_box_price5 || p.band6_special_box_price || "");
  set("band6_special_piece_price", p.offer_piece_price5 || p.band6_special_piece_price || "");
  set("recommendedSellingBoxPrice", p.recommendedSellingBoxPrice);
  set("recommendedSellingPiecePrice", p.recommendedSellingPiecePrice);
  set("old_box_cost", p.cost);
  set("old_piece_cost", p.piece_cost);
  set("old_price", p.price);
  set("margin", marginPct(price, cost));
  set("piece_margin", marginPct(splitPrice, pieceCost));
  set("unit", p.unit);
  set("tax_rate", p.tax_rate || p.vat_id || "");
  set("category", p.category_id ?? p.category ?? p.categorie_id ?? "");
  set("brand", p.brand);
  set("cess_rate", p.cess_rate || p.cess_id || "");
  set("barcode_symbology", p.barcode_symbology);
  set("second_name", p.second_name);
  set("old_image", p.oldimage || p.old_image || "");

  const supplierRows = Array.isArray(suppliers) ? suppliers : [];
  if (supplierRows.length === 0) {
    fd.append("supplier_id[]", "");
  } else {
    supplierRows.forEach((supplier) => {
      const sid = supplier.supplier_id ?? supplier.id ?? "";
      fd.append("supplier_id[]", scalar(sid));
    });
  }
  return { fd, cost, price, pieceCost, splitPrice };
};

export const saveProductListPrice = async ({ productId, columnId, value, row }) => {
  const formField = COLUMN_TO_FORM[columnId];
  if (!formField) {
    throw new Error("Invalid price column");
  }
  const parsed = Number(String(value).trim());
  if (value === "" || value == null || !Number.isFinite(parsed) || parsed < 0) {
    throw new Error("Enter a valid price");
  }
  const nextValue = String(parsed);
  const accessToken = localStorage.getItem("accessToken");
  const jwtToken = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const authToken = jwtToken || accessToken;
  const settings = await getCompanySettings();
  const useEncrypt = resolveCustomerApiEncrypt(settings);
  const resolved = await fetchProductEdit(productId, authToken, useEncrypt);
  const p = resolved.data?.product_data;
  if (!p) {
    throw new Error("Failed to load product");
  }
  const { fd, cost, price, pieceCost, splitPrice } = buildUpdateFormData(
    p,
    resolved.data?.supplier_data,
    productId,
    userId,
    formField,
    nextValue
  );
  let uploadBody = fd;
  if (useEncrypt) {
    const encryptedPayload = encryptPayload(plainObjectFromFormData(fd));
    if (!encryptedPayload) {
      throw new Error("Encryption key missing.");
    }
    uploadBody = new FormData();
    uploadBody.append("encrypted_payload", encryptedPayload);
  }
  const response = await fetch(`${config.API_URL}/update_product`, {
    method: "POST",
    headers: { authorization: `Bearer ${authToken}` },
    body: uploadBody,
  });
  const raw = await response.json();
  const result = normalizeEncryptedApiResponse(raw, useEncrypt);
  if (result.success != true) {
    throw new Error(result.message || "Failed to update price");
  }
  return {
    box_cost: cost,
    price,
    piece_cost: pieceCost,
    piece_price: splitPrice,
    box_diff: formatDiff(price, cost, row?.box_diff),
    piece_diff: formatDiff(splitPrice, pieceCost, row?.piece_diff),
  };
};
